import { EmailViewForm, JFormik } from '@centerpiece/components';
import { Box, Typography } from '@mui/material';
import { useState } from 'react';
import * as Yup from 'yup';
import { FormikHelpers } from 'formik';
import { useLocation } from 'react-router-dom';
import {
  useGetEmailQuery,
  useLazyGetEmailAttachmentQuery,
  useCreateEmailMutation,
  usePatchEmailMutation,
} from 'store/api';
import {
  Attachment,
  Response,
  Action,
  Reaction,
  Contact,
  AppointmentContact,
  MessageContact,
} from 'store/api/models';
import {
  CreateResponseStatus,
  CreateResponseStatusProps,
} from 'statuses/CreateResponseStatus';
import { downloadFile } from 'utils/downloadFile.util';
import {
  useCoordinationReplyForm,
  useEventReplyForm,
} from 'jformik/useReplyForm';
import { toast } from 'react-toastify';
import { useMessageForm } from 'jformik/useMessageForm';
import { useEventForm } from 'jformik/useEventForm';
import {
  appointmentInitialValues,
  messageInitialValues,
  responseInitialValues,
} from 'utils/messaging.utils';
import moment from 'moment';

export const EmailPage = () => {
  const location = useLocation();
  const params = new URLSearchParams(location.search);
  const token = params.get('token');
  const f = params.get('f');
  const {
    data: email,
    isLoading,
    refetch: refetchEmail,
  } = useGetEmailQuery(
    { params: { nested: 'all', code: `=${token}` } },
    { skip: !token }
  );
  const response = (email as MessageContact)?.response_r;
  const message = (email as MessageContact)?.message_r;
  const appointment = (email as AppointmentContact)?.appt_r;
  const [getEmailAttachment] = useLazyGetEmailAttachmentQuery();
  const [createEmail] = useCreateEmailMutation();
  const [patchEmail] = usePatchEmailMutation();
  const [responseStatus, setResponseStatus] = useState<
    Omit<CreateResponseStatusProps, 'close'>
  >({
    open: false,
    loading: false,
    creatingResponse: false,
    creatingResponseError: false,
    attachments: { pending: 0, created: 0, failed: 0 },
  });
  const newcoordinationReply: any[] = useMessageForm({
    reply: !response,
    replied: !!response,
    disabled: !!response,
    onDownload: async (file: any) => {
      const result: Attachment = await getEmailAttachment({
        params: { code: token, attachment: file.id },
      }).unwrap();
      await downloadFile(result);
    },
  });

  const eventReply: any[] = useEventForm({
    reply: !response || !appointment,
    replied: !!response || !!appointment,
    disabled: !!response || !!appointment,
    addDateRange: appointment ? true : undefined,
    notice: !!appointment || !!message,
    onDownload: async (file: any) => {
      const result: Attachment = await getEmailAttachment({
        params: { code: token, attachment: file.id },
      }).unwrap();
      await downloadFile(result);
    },
  });
  return (
    <Box>
      <CreateResponseStatus
        close={() =>
          setResponseStatus({
            open: false,
            loading: false,
            creatingResponse: false,
            creatingResponseError: false,
            attachments: { pending: 0, created: 0, failed: 0 },
          })
        }
        {...responseStatus}
      />
      <EmailViewForm
        date={message?.due_date}
        loading={isLoading}
        caption={
          message && message?.created_at && message?.updated_at ? (
            <Typography variant="caption">
              <small>
                <i>
                  Message{' '}
                  {moment(message.updated_at).isBefore(
                    moment(message.created_at).add(5, 'minutes')
                  )
                    ? `sent`
                    : `updated`}{' '}
                  {`${moment(message?.updated_at).format(
                    'dddd, MMMM Do YYYY, h:mm a'
                  )}`}
                </i>
              </small>
            </Typography>
          ) : undefined
        }
        form={
          message || appointment ? (
            <JFormik
              // debug={true}
              disabled={!!response || responseStatus.open || !!appointment}
              enableReinitialize={!!response || !!appointment}
              initialValues={
                !!response
                  ? responseInitialValues(
                      response,
                      (email as MessageContact)?.message_type,
                      (email as MessageContact)?.contact_r
                    )
                  : !!message
                  ? messageInitialValues(
                      message,
                      (email as MessageContact)?.message_type,
                      (email as MessageContact)?.contact_r
                    )
                  : !!appointment
                  ? appointmentInitialValues(
                      appointment,
                      (email as MessageContact)?.contact_r
                    )
                  : []
              }
              validationSchema={Yup.object().shape({
                form: Yup.object().shape({
                  body: Yup.array().of(
                    Yup.object().shape({
                      action: Yup.object().when('type', (type: string) => {
                        return [
                          'readiness_request',
                          'approval_request',
                        ].includes(type)
                          ? Yup.object().shape({
                              status: Yup.string()
                                .required('Please select an option')
                                .nullable(),
                            })
                          : Yup.object().nullable();
                      }),
                    })
                  ),
                }),
              })}
              handleSubmit={async (values: any, formik: FormikHelpers<any>) => {
                if (!!f) {
                  try {
                    // const newFwd: MessageContact =
                    await patchEmail({
                      ...values,
                      message: values.id,
                      code: token,
                      contact: email?.contact,
                    }).unwrap();
                    toast.info('Message successfully forwarded');
                  } catch (error) {
                    toast.error('Message was unable to be forwarded');
                  }
                } else {
                  const numFiles: number =
                    values?.form?.body?.reduce(
                      (total: number, item: any) =>
                        total +
                          item?.action?.reactionattachments?.filter(
                            (a: any) => !a.id
                          )?.length || 0,
                      0
                    ) ||
                    0 + values?.attachments?.length ||
                    0;
                  setResponseStatus({
                    open: true,
                    loading: true,
                    creatingResponse: true,
                    creatingResponseError: false,
                    attachments: { pending: numFiles, created: 0, failed: 0 },
                  });
                  const msgvalues = {
                    ...values,
                    message: values.id,
                    code: token,
                    contact: email?.contact,
                    attachments: values?.form?.body?.[0]?.replyattachments,
                    reactions: values?.form?.body
                      ?.map((item: any, index: number) => {
                        if (item.type) {
                          return {
                            ...item?.action,
                            action: item.action.id,
                            attachments:
                              item?.action?.reactionattachments?.filter(
                                (a: any) => !a.id
                              ),
                            status: item.action?.status,
                            details: item.action?.reactiondetails,
                            form: {
                              ...item,
                              name: `form.body.${index}.action`,
                            },
                          };
                        }
                      })
                      ?.filter((i: any) => i),
                    type: 'Default',
                  };
                  try {
                    const newResponse: Response = await createEmail(
                      msgvalues
                    ).unwrap();
                    if (newResponse.id > 0) {
                      setResponseStatus(currentStatus => ({
                        ...currentStatus,
                        creatingResponse: false,
                      }));
                      if (newResponse?.reactions?.length > 0) {
                        for (
                          let j = 0;
                          j < newResponse?.reactions.length;
                          j++
                        ) {
                          const reaction: Reaction = newResponse.reactions[j];
                          if (reaction?.attachments?.length > 0) {
                            for (
                              let i = 0;
                              i < reaction.attachments.length;
                              i++
                            ) {
                              const attachment: Attachment =
                                reaction.attachments[i];
                              const file = msgvalues.reactions[
                                j
                              ].attachments?.find(
                                (a: File) => a.name === attachment.name
                              );
                              const res = await fetch(attachment.url, {
                                method: 'PUT',
                                body: file.file,
                              });
                              if (res.ok) {
                                setResponseStatus(currentStatus => ({
                                  ...currentStatus,
                                  attachments: {
                                    pending:
                                      currentStatus.attachments.pending - 1,
                                    created:
                                      currentStatus.attachments.created + 1,
                                    failed: currentStatus.attachments.failed,
                                  },
                                }));
                              } else {
                                setResponseStatus(currentStatus => ({
                                  ...currentStatus,
                                  attachments: {
                                    pending:
                                      currentStatus.attachments.pending - 1,
                                    created: currentStatus.attachments.created,
                                    failed:
                                      currentStatus.attachments.failed + 1,
                                  },
                                }));
                              }
                            }
                          }
                        }
                      }
                      if (newResponse?.attachments?.length > 0) {
                        for (
                          let i = 0;
                          i < newResponse.attachments.length;
                          i++
                        ) {
                          const attachment: Attachment =
                            newResponse.attachments[i];
                          const file = msgvalues.attachments?.find(
                            (a: File) => a.name === attachment.name
                          );
                          const res = await fetch(attachment.url, {
                            method: 'PUT',
                            body: file.file,
                          });
                          if (res.ok) {
                            setResponseStatus(currentStatus => ({
                              ...currentStatus,
                              attachments: {
                                pending: currentStatus.attachments.pending - 1,
                                created: currentStatus.attachments.created + 1,
                                failed: currentStatus.attachments.failed,
                              },
                            }));
                          } else {
                            setResponseStatus(currentStatus => ({
                              ...currentStatus,
                              attachments: {
                                pending: currentStatus.attachments.pending - 1,
                                created: currentStatus.attachments.created,
                                failed: currentStatus.attachments.failed + 1,
                              },
                            }));
                          }
                        }
                      }
                      setResponseStatus(currentStatus => ({
                        ...currentStatus,
                        loading: false,
                      }));
                      refetchEmail();
                    } else {
                      setResponseStatus(currentStatus => ({
                        ...currentStatus,
                        loading: false,
                        creatingResponse: false,
                        creatingResponseError: true,
                      }));
                    }
                  } catch (error) {
                    setResponseStatus(currentStatus => ({
                      ...currentStatus,
                      loading: false,
                      creatingResponse: false,
                      creatingResponseError: true,
                    }));
                    console.error('rejected', error);
                  }
                }
              }}
              containerGridProps={{ spacing: 2 }}
              form={
                message?.type === 'Default'
                  ? newcoordinationReply
                  : message?.type === 'Availability' || appointment
                  ? eventReply
                  : []
              }
            />
          ) : (
            <div>The message has been recalled</div>
          )
        }
      />
    </Box>
  );
};
